import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, ApiError } from "../api";
import { SISTEMAS_EM_ORDEM, nomeDoSistema } from "../sistemas";
import {
  IconSearch,
  IconShield,
  IconAlert,
  IconUsers,
  IconDownload,
  IconEye,
  IconActivity,
  IconGlobe,
  IconFilter,
  IconLogin,
  IconLock,
  IconUnlock,
  IconTrash,
  IconEdit,
  IconPlus,
  IconClock,
  IconMoon,
  IconUserSearch,
  IconArrowRight,
  IconClose,
  IconRefresh,
  IconChart,
  IconCheck,
  IconUpload,
  IconSparkles,
} from "../icons";
import ErrorState from "./ErrorState";

/**
 * AUDITORIA — o painel de segurança da suíte SERCAMP.
 *
 * Só leitura: quem grava são os próprios sistemas, na tabela central do Portal.
 * A tela responde três perguntas, na ordem em que elas aparecem na vida real:
 *
 *   1. "ESTÁ TUDO BEM?" — os quatro indicadores e a FAIXA DE ATIVIDADE, que
 *      mostra os últimos 30 dias. É a faixa que dá sentido ao número de hoje:
 *      440 eventos só quer dizer alguma coisa quando se vê que a média é 300.
 *   2. "O QUE MERECE UM OLHAR?" — os alertas calculados ao vivo (senha errada
 *      em série, exclusão em massa, IP novo, entrada fora de hora). Cada um é
 *      clicável: o clique já deixa a lista de baixo filtrada naquele caso.
 *   3. "O QUE ACONTECEU?" — a linha do tempo, agrupada por dia, e o DOSSIÊ de
 *      quem aparece nela (tudo o que a pessoa fez, em todos os sistemas).
 *
 * A regra de cor é sóbria de propósito: verde é o normal, âmbar é "olhe isto",
 * e vermelho fica reservado para falha de verdade — senão a tela vira um painel
 * de alarme onde tudo grita igual e nada se destaca.
 */

/* ------------------------------------------------------------------ *
 *  TEMPO — a auditoria é lida "de hoje para trás"                     *
 * ------------------------------------------------------------------ */

/** Data e hora completas (para o detalhe e para os rodapés). */
function quando(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}

function horaDe(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

/** "14/09" — para os extremos do gráfico. */
function dataCurta(iso: string | null): string {
  if (!iso) return "—";
  const s = String(iso).slice(0, 10).split("-");
  return s.length === 3 ? s[2] + "/" + s[1] : String(iso);
}

/**
 * O DIA DO EVENTO, no fuso de quem está olhando.
 *
 * `sv-SE` é o atalho para "aaaa-mm-dd" já convertido para a hora local — usar
 * `toISOString().slice(0,10)` jogaria os eventos da noite para o dia seguinte
 * (o banco guarda em UTC), e a linha do tempo mostraria "amanhã" no topo.
 */
function chaveDoDia(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("sv-SE");
}

/** "Hoje", "Ontem" ou "sexta-feira, 12 de setembro". */
function rotuloDoDia(chave: string): string {
  const hoje = new Date().toLocaleDateString("sv-SE");
  if (chave === hoje) return "Hoje";
  const ontem = new Date(Date.now() - 86400000).toLocaleDateString("sv-SE");
  if (chave === ontem) return "Ontem";
  const [a, m, d] = chave.split("-").map(Number);
  if (!a) return chave;
  return new Date(a, m - 1, d).toLocaleDateString("pt-BR", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
}

/** "agora", "há 6 min", "há 2 h", "há 3 dias" — o tempo que o olho lê primeiro. */
function relativo(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const seg = Math.round((Date.now() - t) / 1000);
  if (seg < 60) return "agora";
  if (seg < 3600) return "há " + Math.floor(seg / 60) + " min";
  if (seg < 86400) return "há " + Math.floor(seg / 3600) + " h";
  const dias = Math.floor(seg / 86400);
  return dias === 1 ? "há 1 dia" : "há " + dias + " dias";
}

/* ------------------------------------------------------------------ *
 *  AS AÇÕES — cada uma com verbo, ícone e rótulo em português          *
 * ------------------------------------------------------------------ */

type Tom = "" | "del" | "falha";

interface Acao {
  rotulo: string;
  verbo: string;
  Icone: (p: { size?: number }) => JSX.Element;
  tom: Tom;
}

/**
 * O vocabulário da auditoria. Os sistemas gravam a ação em inglês técnico
 * (`delete`, `update`…) porque é o que a tabela central padroniza; aqui ela
 * vira frase em português — "excluiu", "alterou" — que é o que se lê de longe.
 * Ação que não estiver nesta lista continua aparecendo crua, e não some.
 */
const ACOES: Record<string, Acao> = {
  login_sucesso: { rotulo: "Login", verbo: "entrou em", Icone: IconLogin, tom: "" },
  login_falha: { rotulo: "Falha de login", verbo: "errou a senha em", Icone: IconLock, tom: "falha" },
  logout: { rotulo: "Saída", verbo: "saiu de", Icone: IconUnlock, tom: "" },
  create: { rotulo: "Criação", verbo: "criou", Icone: IconPlus, tom: "" },
  update: { rotulo: "Alteração", verbo: "alterou", Icone: IconEdit, tom: "" },
  delete: { rotulo: "Exclusão", verbo: "excluiu", Icone: IconTrash, tom: "del" },
  export: { rotulo: "Exportação", verbo: "exportou", Icone: IconDownload, tom: "" },
  exportar: { rotulo: "Exportação", verbo: "exportou", Icone: IconDownload, tom: "" },
};

/**
 * O RESTO DO VOCABULÁRIO — as ações próprias de cada sistema.
 *
 * Além das seis ações padrão, os sistemas gravam o nome do que fizeram:
 * `concluir_ensaio`, `liberar_resultados_os`, `revogar_permissao`,
 * `sugerir_diagnostico_ia`… São dezenas, nascem novas a cada entrega, e listar
 * uma por uma aqui seria uma lista sempre desatualizada. Como quase todas
 * começam pelo verbo, o ÍCONE sai do prefixo: assim uma ação que este arquivo
 * nunca viu já aparece com a cara certa — exclusão com lixeira, liberação com
 * cadeado aberto — sem ninguém precisar vir aqui cadastrar.
 */
const PREFIXOS: [RegExp, { Icone: Acao["Icone"]; tom?: Tom }][] = [
  [/^(excluir|remover|apagar|limpar|descartar)/, { Icone: IconTrash, tom: "del" }],
  [/^(criar|gerar|nov[ao]|adicionar|incluir|abrir)/, { Icone: IconPlus }],
  [/^(salvar|atualizar|editar|alterar|config|marcar|registrar|importar)/, { Icone: IconEdit }],
  [/^(liberar|conceder|aprovar|autorizar|desbloquear)/, { Icone: IconUnlock }],
  [/^(revogar|bloquear|reprovar|recusar|cancelar|inativar)/, { Icone: IconLock }],
  [/^(concluir|finalizar|encerrar|entregar|responder)/, { Icone: IconCheck }],
  [/^(reabrir|reenviar|repetir|restaurar)/, { Icone: IconRefresh }],
  [/^(upload|anexar|enviar)/, { Icone: IconUpload }],
  [/^(despachar|solicitar|encaminhar|resolver)/, { Icone: IconArrowRight }],
  [/^(login|entrar|acesso)/, { Icone: IconLogin }],
  [/(_ia|_automatico)$/, { Icone: IconSparkles }],
];

function daAcao(acao: string): Acao {
  const conhecida = ACOES[acao];
  if (conhecida) return conhecida;

  const crua = String(acao ?? "").trim();
  const legivel = crua.replace(/_/g, " ");
  const achado = PREFIXOS.find(([re]) => re.test(crua))?.[1];
  return {
    // "concluir ensaio" → "Concluir ensaio": vira rótulo de tela, não código.
    rotulo: legivel.charAt(0).toUpperCase() + legivel.slice(1),
    // Sem verbo: a frase mostra a própria ação, em vez de inventar conjugação
    // ("concluir_ensaio" não vira "concluiu o ensaio" sem chutar).
    verbo: "",
    Icone: achado?.Icone ?? IconActivity,
    tom: achado?.tom ?? ("" as Tom),
  };
}

/** "usuarios #39" — o alvo do evento, quando o sistema de origem informou. */
function recursoDe(e: { recurso_tipo?: string | null; recurso_id?: string | null }): string {
  if (!e.recurso_tipo) return "";
  return e.recurso_tipo + (e.recurso_id ? " #" + e.recurso_id : "");
}

/**
 * Onde um valor sem espaço pode quebrar de linha.
 *
 * Um e-mail como `admin@portalsercamp.com` é uma palavra só: o navegador não
 * acha onde quebrar e o texto vaza para fora da moldura do cartão. Aqui
 * partimos em pedaços nos pontos onde a quebra continua legível — antes do
 * "@" e depois de ponto, traço, barra, "#" e ":" — para o <wbr> entrar entre
 * eles. O <wbr> só quebra se faltar espaço; cabendo, o valor segue numa
 * linha só.
 */
function pedacosDe(texto: string): string[] {
  const pedacos: string[] = [];
  let atual = "";
  for (const ch of texto) {
    if (ch === "@" && atual) {
      pedacos.push(atual);
      atual = "";
    }
    atual += ch;
    if (".-_/#:".includes(ch)) {
      pedacos.push(atual);
      atual = "";
    }
  }
  if (atual) pedacos.push(atual);
  return pedacos;
}

/** O valor de texto livre dentro do cartão, com as quebras já marcadas. */
function ValorLongo({ texto }: { texto: string }) {
  return (
    <>
      {pedacosDe(texto).map((pedaco, i) => (
        <Fragment key={i}>
          {i > 0 && <wbr />}
          {pedaco}
        </Fragment>
      ))}
    </>
  );
}

/* ------------------------------------------------------------------ *
 *  GRÁFICOS — feitos à mão: o portal não usa biblioteca de gráfico     *
 * ------------------------------------------------------------------ */

interface Ponto {
  dia: string;
  eventos: number;
  falhas: number;
}

/**
 * A FAIXA DE ATIVIDADE — uma barra por dia.
 *
 * É HTML, não SVG, de propósito: barras em flex acompanham qualquer largura de
 * tela sem esticar traço nem texto, que é o defeito clássico do SVG com
 * `preserveAspectRatio="none"`. O dia sem nenhum evento vira um traço rente à
 * base — some da vista, mas não some do eixo, e é isso que deixa ver o fim de
 * semana parado no meio da semana cheia.
 */
function FaixaAtividade({ dados }: { dados: Ponto[] }) {
  if (!dados || dados.length === 0) return <p className="aud-vazio">Sem histórico no período.</p>;
  const maior = Math.max(...dados.map((d) => d.eventos), 1);
  const total = dados.reduce((s, d) => s + d.eventos, 0);
  const pico = dados.reduce((a, b) => (b.eventos > a.eventos ? b : a), dados[0]);
  const media = Math.round(total / dados.length);

  return (
    <>
      {/* A LINHA DA MÉDIA. Sem ela, uma barra alta no meio de barras baixas não
          diz se foi um dia movimentado ou um dia fora da curva — e é essa
          diferença que faz alguém ir olhar o que aconteceu naquele dia. */}
      <div className="aud-graf" style={{ position: "relative" }}>
        {media > 0 && (
          <span
            className="aud-graf-media"
            style={{ bottom: (media / maior) * 100 + "%" }}
            title={"média de " + media + " eventos por dia"}
          />
        )}
        {dados.map((d) => {
          const alturaEventos = (d.eventos / maior) * 100;
          const alturaFalhas = (d.falhas / maior) * 100;
          return (
            <div
              key={d.dia}
              className="aud-graf-col"
              title={
                dataCurta(d.dia) +
                ": " +
                d.eventos +
                " evento(s)" +
                (d.falhas > 0 ? " · " + d.falhas + " falha(s) de login" : "")
              }
            >
              <span className="aud-graf-barra" style={{ height: Math.max(alturaEventos, 1.5) + "%" }}>
                {d.falhas > 0 && (
                  <span className="aud-graf-falha" style={{ height: Math.max(alturaFalhas, 6) + "%" }} />
                )}
              </span>
            </div>
          );
        })}
      </div>
      <div className="aud-graf-eixo">
        <span>{dataCurta(dados[0].dia)}</span>
        <span>{dataCurta(dados[dados.length - 1].dia)}</span>
      </div>
      <div className="aud-legenda">
        <span>
          <i className="aud-legenda-eventos" />
          eventos por dia
        </span>
        <span>
          <i className="aud-legenda-falhas" />
          falhas de login
        </span>
        <small>
          média de {media}/dia · pico de {pico.eventos} em {dataCurta(pico.dia)}
        </small>
      </div>
    </>
  );
}

/**
 * O FIO DENTRO DO CARTÃO — a mesma série, do tamanho de uma assinatura.
 *
 * Aqui o SVG cabe bem porque não há texto nenhum para distorcer, e o traço se
 * protege com `vector-effect`, que mantém a espessura mesmo com a caixa
 * esticada em telas largas.
 */
function Fio({ dados }: { dados: Ponto[] }) {
  if (!dados || dados.length < 2) return null;
  const maior = Math.max(...dados.map((d) => d.eventos), 1);
  const passo = 100 / (dados.length - 1);
  const pontos = dados.map((d, i) => i * passo + "," + (100 - (d.eventos / maior) * 100));

  return (
    <svg className="aud-spark" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
      <polygon className="aud-spark-area" points={"0,100 " + pontos.join(" ") + " 100,100"} />
      <polyline className="aud-spark-linha" points={pontos.join(" ")} vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

/**
 * Lista "nome — barra — número", usada nos recortes por sistema/ação/IP.
 *
 * O `teto` existe porque uma conta antiga acumula dezenas de tipos de ação, e a
 * lista inteira empurra todo o resto do dossiê para fora da tela. Mostra as
 * maiores e diz quantas ficaram de fora — nada some sem aviso.
 */
function Ranking({
  itens,
  aoClicar,
  teto = 8,
}: {
  itens: { chave: string; nome: string; valor: number; nota?: string }[];
  aoClicar?: (chave: string) => void;
  teto?: number;
}) {
  if (itens.length === 0) return <p className="aud-vazio">Nada no período.</p>;
  const maior = Math.max(...itens.map((i) => i.valor), 1);
  const visiveis = itens.slice(0, teto);
  const sobra = itens.length - visiveis.length;
  return (
    <ul className="dev-barras">
      {visiveis.map((i) => (
        <li key={i.chave}>
          {aoClicar ? (
            <button className="dev-barra-nome" onClick={() => aoClicar(i.chave)}>
              {i.nome}
            </button>
          ) : (
            <span className="dev-barra-nome">{i.nome}</span>
          )}
          <span className="dev-barra-trilho">
            <span className="dev-barra-fill" style={{ width: Math.round((i.valor / maior) * 100) + "%" }} />
          </span>
          <span className="dev-barra-valor">
            {i.valor}
            {i.nota ? <small className="muted"> · {i.nota}</small> : null}
          </span>
        </li>
      ))}
      {sobra > 0 && (
        <li className="aud-vazio" style={{ padding: "4px 0" }}>
          e mais {sobra} {sobra === 1 ? "item" : "itens"}
        </li>
      )}
    </ul>
  );
}

/* ------------------------------------------------------------------ *
 *  ALERTAS                                                             *
 * ------------------------------------------------------------------ */

interface AlertaNaTela {
  chave: string;
  gravidade: "grave" | "atencao" | "";
  tipo: string;
  Icone: (p: { size?: number }) => JSX.Element;
  titulo: string;
  sub: string;
  /** O que a lista de baixo passa a mostrar quando se clica no alerta. */
  filtro: { sistema?: string; usuario?: string; acao?: string; ip?: string };
}

/**
 * Achata as quatro famílias de alerta numa lista só, já ordenada por gravidade.
 *
 * SÓ A FORÇA BRUTA É VERMELHA: senha errada em série é a única das quatro que
 * indica alguém tentando entrar. Exclusão em massa é âmbar (quase sempre é uma
 * limpeza legítima, mas ninguém quer descobrir tarde), e IP novo / fora de
 * horário são neutros — acontecem toda semana com quem viaja ou vira a noite.
 */
function montarAlertas(a: any): AlertaNaTela[] {
  if (!a) return [];
  const lista: AlertaNaTela[] = [];

  for (const [i, x] of (a.forcaBruta ?? []).entries()) {
    lista.push({
      chave: "fb" + i,
      gravidade: "grave",
      tipo: "senha errada em série",
      Icone: IconLock,
      titulo: (x.usuario || "usuário desconhecido") + " · " + nomeDoSistema(x.sistema),
      sub:
        x.tentativas +
        " tentativas em 15 minutos" +
        (x.ip ? " · IP " + x.ip : "") +
        " · " +
        quando(x.ultimo),
      filtro: { sistema: x.sistema, usuario: x.usuario || undefined, acao: "login_falha" },
    });
  }

  for (const [i, x] of (a.exclusoesEmMassa ?? []).entries()) {
    lista.push({
      chave: "ex" + i,
      gravidade: "atencao",
      tipo: "exclusão em massa",
      Icone: IconTrash,
      titulo: (x.usuario || "—") + " · " + nomeDoSistema(x.sistema),
      sub:
        x.exclusoes +
        " exclusões em uma hora" +
        (x.recurso ? " · " + x.recurso : "") +
        " · " +
        quando(x.hora),
      filtro: { sistema: x.sistema, usuario: x.usuario || undefined, acao: "delete" },
    });
  }

  for (const [i, x] of (a.ipNovo ?? []).entries()) {
    lista.push({
      chave: "ip" + i,
      gravidade: "",
      tipo: "IP novo",
      Icone: IconGlobe,
      titulo: x.usuario + " · " + nomeDoSistema(x.sistema),
      sub: "primeiro acesso do IP " + x.ip + " · " + quando(x.quando),
      filtro: { sistema: x.sistema, usuario: x.usuario, ip: x.ip },
    });
  }

  for (const [i, x] of (a.horarioIncomum ?? []).entries()) {
    lista.push({
      chave: "hr" + i,
      gravidade: "",
      tipo: "fora de horário",
      Icone: IconMoon,
      titulo: x.usuario + " · " + nomeDoSistema(x.sistema),
      sub: "entrou em " + quando(x.criado_em) + (x.ip ? " · IP " + x.ip : ""),
      filtro: { sistema: x.sistema, usuario: x.usuario, acao: "login_sucesso" },
    });
  }

  return lista;
}

/* ------------------------------------------------------------------ *
 *  A TELA                                                              *
 * ------------------------------------------------------------------ */

/** Os recortes que a tela sabe pedir — um objeto só, para caber num `useEffect`. */
interface Filtros {
  sistema: string;
  acao: string;
  horas: string;
  q: string;
  usuario: string;
  ip: string;
  recurso_tipo: string;
  recurso_id: string;
  de: string;
  ate: string;
}

const FILTROS_VAZIOS: Filtros = {
  sistema: "todos",
  acao: "todos",
  horas: "168",
  q: "",
  usuario: "",
  ip: "",
  recurso_tipo: "",
  recurso_id: "",
  de: "",
  ate: "",
};

/** Só o que está preenchido vira query string (o `qs` já descarta "todos"). */
function paraUrl(f: Filtros): Record<string, string> {
  const url: Record<string, string> = {
    sistema: f.sistema,
    acao: f.acao,
    q: f.q,
    usuario: f.usuario,
    ip: f.ip,
    recurso_tipo: f.recurso_tipo,
    recurso_id: f.recurso_id,
  };
  // Período por DATA manda no período por horas — os dois juntos brigariam.
  if (f.de || f.ate) {
    url.de = f.de;
    url.ate = f.ate;
  } else {
    url.horas = f.horas;
  }
  return url;
}

export default function AuditoriaView() {
  const [resumo, setResumo] = useState<any | null>(null);
  const [alertas, setAlertas] = useState<any | null>(null);
  const [relatorio, setRelatorio] = useState<any | null>(null);
  const [eventos, setEventos] = useState<any[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [f, setF] = useState<Filtros>(FILTROS_VAZIOS);
  const [detalhe, setDetalhe] = useState<any | null>(null);
  const [dossie, setDossie] = useState<any | null>(null);
  const [carregandoDossie, setCarregandoDossie] = useState(false);
  const listaRef = useRef<HTMLDivElement | null>(null);

  const mudar = useCallback((patch: Partial<Filtros>) => setF((atual) => ({ ...atual, ...patch })), []);

  /* O RESUMO e os ALERTAS têm ciclo próprio: recarregá-los a cada tecla digitada
     na busca faria os quatro cartões e o gráfico piscarem sem motivo — eles não
     dependem do texto buscado, só do sistema escolhido. */
  const carregarTopo = useCallback(async () => {
    try {
      const [r, a] = await Promise.all([
        api.auditoria.resumo(),
        api.auditoria.alertas(f.sistema === "todos" ? undefined : f.sistema),
      ]);
      setResumo(r);
      setAlertas(a);
      setRelatorio(f.sistema === "todos" ? null : await api.auditoria.doSistema(f.sistema, 30));
      setErro(null);
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Não foi possível carregar a auditoria.");
    }
  }, [f.sistema]);

  useEffect(() => {
    carregarTopo();
  }, [carregarTopo]);

  /* A LISTA — esta sim segue todos os filtros, com uma pausa para a digitação. */
  const carregarEventos = useCallback(async () => {
    setCarregando(true);
    try {
      setEventos((await api.auditoria.listar(paraUrl(f))) as any[]);
      setErro(null);
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Não foi possível carregar a auditoria.");
    } finally {
      setCarregando(false);
    }
  }, [f]);

  useEffect(() => {
    const t = setTimeout(carregarEventos, f.q ? 350 : 0);
    return () => clearTimeout(t);
  }, [carregarEventos, f.q]);

  async function abrirDossie(usuario: string) {
    if (!usuario) return;
    setDetalhe(null);
    setCarregandoDossie(true);
    setDossie({ usuario, carregando: true });
    try {
      setDossie(await api.auditoria.doUsuario(usuario, 30));
    } catch (err) {
      setDossie(null);
      alert(err instanceof ApiError ? err.message : "Não foi possível abrir o dossiê.");
    } finally {
      setCarregandoDossie(false);
    }
  }

  /** Abre o CSV numa aba: quem salva o arquivo (com o nome certo) é o navegador. */
  function exportar(recorte: Record<string, string>) {
    window.open(api.auditoria.urlExportar(recorte), "_blank", "noopener");
  }

  /** O clique no alerta já entrega a lista filtrada naquele caso. */
  function investigar(filtro: AlertaNaTela["filtro"]) {
    setF((atual) => ({
      ...atual,
      sistema: filtro.sistema ?? atual.sistema,
      usuario: filtro.usuario ?? "",
      acao: filtro.acao ?? "todos",
      ip: filtro.ip ?? "",
      // Alerta é sempre coisa recente: 7 dias cobre as quatro famílias.
      horas: "168",
      de: "",
      ate: "",
    }));
    listaRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  const listaAlertas = useMemo(() => montarAlertas(alertas), [alertas]);
  const graves = listaAlertas.filter((a) => a.gravidade === "grave").length;

  /* A LINHA DO TEMPO — os eventos já vêm do servidor em ordem decrescente, e
     aqui só ganham a quebra por dia. Um `Map` preserva essa ordem, então o
     agrupamento não precisa reordenar nada. */
  const porDia = useMemo(() => {
    const mapa = new Map<string, any[]>();
    for (const e of eventos) {
      const k = chaveDoDia(e.criado_em);
      const atual = mapa.get(k);
      if (atual) atual.push(e);
      else mapa.set(k, [e]);
    }
    return [...mapa.entries()];
  }, [eventos]);

  const serie: Ponto[] = relatorio?.porDia ?? resumo?.porDia ?? [];
  const filtrado =
    f.sistema !== "todos" ||
    f.acao !== "todos" ||
    !!f.q ||
    !!f.usuario ||
    !!f.ip ||
    !!f.recurso_tipo ||
    !!f.de ||
    !!f.ate;

  if (erro && !resumo) return <ErrorState message={erro} onRetry={carregarTopo} />;

  return (
    <>
      {/* ---------- OS QUATRO INDICADORES ---------- */}
      {resumo && (
        <div className="cards">
          <div className="card tone-green">
            <span className="card-icon">
              <IconActivity size={20} />
            </span>
            <span className="card-body">
              <span className="card-label">Eventos (24h)</span>
              <span className="card-value">{resumo.totais.eventos_24h}</span>
              <small className="muted">{resumo.totais.eventos_7d} nos últimos 7 dias</small>
              <Fio dados={resumo.porDia ?? []} />
            </span>
          </div>

          {/* O tom acompanha o fato: zero falha é um cartão calmo, não âmbar. */}
          <div
            className={
              "card " +
              (resumo.totais.falhas_24h > 5
                ? "tone-red"
                : resumo.totais.falhas_24h > 0
                  ? "tone-amber"
                  : "tone-lime")
            }
          >
            <span className="card-icon">
              <IconLock size={20} />
            </span>
            <span className="card-body">
              <span className="card-label">Falhas de login (24h)</span>
              <span className="card-value">{resumo.totais.falhas_24h}</span>
              <small className="muted">senha errada ou usuário inexistente</small>
              {resumo.totais.falhas_24h > 5 && (
                <small className="aud-card-nota forte">acima do normal — veja os alertas</small>
              )}
            </span>
          </div>

          <div className="card tone-teal">
            <span className="card-icon">
              <IconUsers size={20} />
            </span>
            <span className="card-body">
              <span className="card-label">Pessoas ativas (24h)</span>
              <span className="card-value">{resumo.totais.usuarios_24h}</span>
              <small className="muted">
                em {(resumo.porSistema ?? []).length} sistema(s) nos últimos 7 dias
              </small>
            </span>
          </div>

          <div className={"card " + (listaAlertas.length > 0 ? "tone-amber" : "tone-lime")}>
            <span className="card-icon">
              <IconShield size={20} />
            </span>
            <span className="card-body">
              <span className="card-label">Alertas abertos</span>
              <span className="card-value">{listaAlertas.length}</span>
              <small className="muted">
                {listaAlertas.length === 0
                  ? "nada fora do padrão agora"
                  : graves > 0
                    ? graves + " grave(s) · veja abaixo"
                    : "para conferir · veja abaixo"}
              </small>
            </span>
          </div>
        </div>
      )}

      {/* ---------- A FAIXA DE ATIVIDADE ---------- */}
      {serie.length > 0 && (
        <section className="aud-bloco">
          <div className="aud-head">
            <span className="aud-head-icone">
              <IconChart size={17} />
            </span>
            <span className="aud-head-txt">
              <h3>
                {relatorio ? "Atividade de " + nomeDoSistema(relatorio.sistema) : "Atividade da suíte"}
              </h3>
              <small>
                {relatorio
                  ? "últimos " + relatorio.janela + " dias · " + relatorio.totais.eventos + " eventos no total"
                  : "últimos 30 dias, todos os sistemas"}
              </small>
            </span>
            <span className="aud-head-acao">
              <button className="btn-secondary btn-sm" onClick={carregarTopo} title="Recarregar os números">
                <IconRefresh size={14} /> Atualizar
              </button>
            </span>
          </div>
          <FaixaAtividade dados={serie} />
        </section>
      )}

      {/* ---------- O QUE MERECE UM OLHAR ---------- */}
      {listaAlertas.length > 0 && (
        <section className="aud-bloco">
          <div className="aud-head">
            <span className="aud-head-icone">
              <IconAlert size={17} />
            </span>
            <span className="aud-head-txt">
              <h3>O que merece um olhar</h3>
              <small>calculado agora — some sozinho quando a situação passa</small>
            </span>
          </div>
          <ul className="aud-alertas">
            {listaAlertas.map((a) => (
              <li key={a.chave}>
                <button
                  className={"aud-alerta " + a.gravidade}
                  onClick={() => investigar(a.filtro)}
                  title="Ver os eventos deste caso"
                >
                  <span className="aud-alerta-icone">
                    <a.Icone size={18} />
                  </span>
                  <span className="aud-alerta-corpo">
                    <span className="aud-alerta-tipo">{a.tipo}</span>
                    <span className="aud-alerta-titulo">{a.titulo}</span>
                    <span className="aud-alerta-sub">{a.sub}</span>
                  </span>
                  <span className="aud-alerta-seta">
                    <IconArrowRight size={16} />
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ---------- MOVIMENTO POR SISTEMA (quando nenhum está escolhido) ---------- */}
      {!relatorio && resumo && (resumo.porSistema ?? []).length > 0 && (
        <section className="aud-bloco">
          <div className="aud-head">
            <span className="aud-head-icone">
              <IconActivity size={17} />
            </span>
            <span className="aud-head-txt">
              <h3>Movimento por sistema</h3>
              <small>últimos 7 dias · clique para ver o relatório e exportar a auditoria inteira dele</small>
            </span>
          </div>
          <div className="aud-sistemas">
            {resumo.porSistema.map((s: any) => {
              const maior = Math.max(...resumo.porSistema.map((x: any) => x.eventos), 1);
              return (
                <button key={s.sistema} className="aud-sistema" onClick={() => mudar({ sistema: s.sistema })}>
                  <span className="aud-sistema-nome">{nomeDoSistema(s.sistema)}</span>
                  <span className="aud-sistema-linha">
                    <span className="aud-sistema-valor">{s.eventos}</span>
                    <span className="aud-sistema-unidade">eventos</span>
                  </span>
                  <span className="aud-sistema-trilho">
                    <span
                      className="aud-sistema-fill"
                      style={{ width: Math.max(2, Math.round((s.eventos / maior) * 100)) + "%" }}
                    />
                  </span>
                  <span className="aud-sistema-pe">
                    <span>{s.usuarios} pessoa(s)</span>
                    {s.falhas > 0 && <span className="falha">{s.falhas} falha(s)</span>}
                  </span>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* ---------- O RELATÓRIO DO SISTEMA ESCOLHIDO ---------- */}
      {relatorio && (
        <section className="aud-bloco">
          <div className="aud-head">
            <span className="aud-head-icone">
              <IconShield size={17} />
            </span>
            <span className="aud-head-txt">
              <h3>Relatório de {nomeDoSistema(relatorio.sistema)}</h3>
              <small>
                últimos {relatorio.janela} dias · registro desde {quando(relatorio.totais.primeiro)} · último
                evento {quando(relatorio.totais.ultimo)}
              </small>
            </span>
            <span className="aud-head-acao">
              <button className="btn-secondary btn-sm" onClick={() => mudar({ sistema: "todos" })}>
                <IconClose size={14} /> Ver todos
              </button>
            </span>
          </div>

          <div className="aud-dossie-num">
            <span className="aud-num">
              <b>{relatorio.totais.eventos_periodo}</b>
              <span>eventos no período</span>
            </span>
            <span className="aud-num">
              <b>{relatorio.totais.eventos}</b>
              <span>desde o início</span>
            </span>
            <span className="aud-num">
              <b>{relatorio.totais.usuarios_periodo}</b>
              <span>pessoas</span>
            </span>
            <span className="aud-num">
              <b>{relatorio.totais.ips_periodo}</b>
              <span>IPs</span>
            </span>
            <span className={"aud-num" + (relatorio.totais.falhas_periodo > 0 ? " alerta" : "")}>
              <b>{relatorio.totais.falhas_periodo}</b>
              <span>falhas de login</span>
            </span>
          </div>

          <div className="aud-duas">
            <div>
              <h4 className="aud-mini-titulo">Quem mais mexe</h4>
              {(relatorio.porUsuario ?? []).length === 0 ? (
                <p className="aud-vazio">Sem atividade de pessoa no período.</p>
              ) : (
                <ul className="aud-ips">
                  {relatorio.porUsuario.map((u: any) => (
                    <li key={u.usuario}>
                      <IconUserSearch size={15} />
                      <button
                        className="aud-evento-quem"
                        onClick={() => abrirDossie(u.usuario)}
                        title="Abrir o dossiê desta pessoa"
                      >
                        {u.usuario}
                      </button>
                      <span className="aud-ip-nota">
                        {u.eventos} evento(s)
                        {u.exclusoes > 0 ? " · " + u.exclusoes + " exclusão(ões)" : ""}
                        {u.falhas > 0 ? " · " + u.falhas + " falha(s)" : ""}
                      </span>
                    </li>
                  ))}
                </ul>
              )}

              <h4 className="aud-mini-titulo" style={{ marginTop: 18 }}>
                O que mais acontece
              </h4>
              <Ranking
                itens={(relatorio.porAcao ?? []).map((a: any) => ({
                  chave: a.acao,
                  nome: daAcao(a.acao).rotulo,
                  valor: a.eventos,
                }))}
                aoClicar={(acao) => mudar({ acao })}
              />
            </div>

            <div>
              <h4 className="aud-mini-titulo">Onde se mexe mais</h4>
              <Ranking
                itens={(relatorio.porRecurso ?? []).map((r: any) => ({
                  chave: r.recurso_tipo,
                  nome: r.recurso_tipo,
                  valor: r.eventos,
                }))}
                aoClicar={(recurso_tipo) => mudar({ recurso_tipo, recurso_id: "" })}
                teto={10}
              />

              <h4 className="aud-mini-titulo" style={{ marginTop: 18 }}>
                De onde vem
              </h4>
              {(relatorio.porIp ?? []).length === 0 ? (
                <p className="aud-vazio">Sem IP registrado.</p>
              ) : (
                <ul className="aud-ips">
                  {relatorio.porIp.map((i: any) => (
                    <li key={i.ip}>
                      <IconGlobe size={15} />
                      <strong>{i.ip}</strong>
                      <span className="aud-ip-nota">
                        {i.eventos} evento(s) · {i.usuarios} pessoa(s)
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ---------- A LINHA DO TEMPO ---------- */}
      <section className="aud-bloco" ref={listaRef}>
        <div className="aud-head">
          <span className="aud-head-icone">
            <IconClock size={17} />
          </span>
          <span className="aud-head-txt">
            <h3>Os eventos</h3>
            <small>
              {carregando
                ? "carregando…"
                : eventos.length +
                  " na tela" +
                  (eventos.length >= 200 ? " (os mais recentes — exporte para ver tudo)" : "") +
                  (filtrado ? " · com filtro" : "")}
            </small>
          </span>
        </div>

      {/* ---------- OS FILTROS ---------- */}
      <div className="aud-filtros">
        <div className="search-wrap">
          <span className="search-icone">
            <IconSearch size={16} />
          </span>
          <input
            className="search"
            placeholder="Buscar por pessoa, recurso, IP ou detalhe…"
            value={f.q}
            onChange={(e) => mudar({ q: e.target.value })}
          />
        </div>

        <select value={f.sistema} onChange={(e) => mudar({ sistema: e.target.value })}>
          <option value="todos">Todos os sistemas</option>
          {SISTEMAS_EM_ORDEM.map((s) => (
            <option key={s.id} value={s.id}>
              {s.nome}
            </option>
          ))}
        </select>

        <select value={f.acao} onChange={(e) => mudar({ acao: e.target.value })}>
          <option value="todos">Todas as ações</option>
          <option value="login_sucesso">Login</option>
          <option value="login_falha">Falha de login</option>
          <option value="create">Criação</option>
          <option value="update">Alteração</option>
          <option value="delete">Exclusão</option>
        </select>

        <select
          value={f.de || f.ate ? "datas" : f.horas}
          onChange={(e) => {
            const v = e.target.value;
            if (v === "datas") {
              // Abre o período por data já preenchido com os últimos 7 dias.
              const hoje = new Date();
              const antes = new Date(Date.now() - 7 * 86400000);
              mudar({ de: antes.toLocaleDateString("sv-SE"), ate: hoje.toLocaleDateString("sv-SE") });
            } else {
              mudar({ horas: v, de: "", ate: "" });
            }
          }}
        >
          <option value="24">Últimas 24h</option>
          <option value="168">7 dias</option>
          <option value="720">30 dias</option>
          <option value="2160">90 dias</option>
          <option value="datas">Período…</option>
        </select>

        {(f.de || f.ate) && (
          <span className="aud-datas">
            <input type="date" value={f.de} max={f.ate || undefined} onChange={(e) => mudar({ de: e.target.value })} />
            até
            <input type="date" value={f.ate} min={f.de || undefined} onChange={(e) => mudar({ ate: e.target.value })} />
          </span>
        )}

        <button
          className="btn-secondary btn-sm"
          title="Baixar em planilha exatamente o que está filtrado"
          onClick={() => exportar(paraUrl(f))}
        >
          <IconDownload size={15} /> Exportar
        </button>
        {f.sistema !== "todos" && (
          <button
            className="btn-primary btn-sm"
            title={"Baixar TODA a auditoria de " + nomeDoSistema(f.sistema) + ", sem limite de período"}
            onClick={() => exportar({ sistema: f.sistema })}
          >
            <IconDownload size={15} /> Tudo de {nomeDoSistema(f.sistema)}
          </button>
        )}
      </div>

      {/* Os filtros ativos, em pílulas: clicar tira o filtro. Sem isto, é fácil
          olhar uma lista curta e não perceber que ela está recortada. */}
      {filtrado && (
        <div className="aud-chips">
          <IconFilter size={14} />
          {f.sistema !== "todos" && (
            <button className="aud-chip" onClick={() => mudar({ sistema: "todos" })}>
              sistema: <b>{nomeDoSistema(f.sistema)}</b> <IconClose size={12} />
            </button>
          )}
          {f.usuario && (
            <button className="aud-chip" onClick={() => mudar({ usuario: "" })}>
              pessoa: <b>{f.usuario}</b> <IconClose size={12} />
            </button>
          )}
          {f.acao !== "todos" && (
            <button className="aud-chip" onClick={() => mudar({ acao: "todos" })}>
              ação: <b>{daAcao(f.acao).rotulo}</b> <IconClose size={12} />
            </button>
          )}
          {f.ip && (
            <button className="aud-chip" onClick={() => mudar({ ip: "" })}>
              IP: <b>{f.ip}</b> <IconClose size={12} />
            </button>
          )}
          {f.recurso_tipo && (
            <button className="aud-chip" onClick={() => mudar({ recurso_tipo: "", recurso_id: "" })}>
              recurso: <b>{f.recurso_tipo + (f.recurso_id ? " #" + f.recurso_id : "")}</b>{" "}
              <IconClose size={12} />
            </button>
          )}
          {f.q && (
            <button className="aud-chip" onClick={() => mudar({ q: "" })}>
              busca: <b>{f.q}</b> <IconClose size={12} />
            </button>
          )}
          {(f.de || f.ate) && (
            <button className="aud-chip" onClick={() => mudar({ de: "", ate: "", horas: "168" })}>
              período: <b>{dataCurta(f.de) + " a " + dataCurta(f.ate)}</b> <IconClose size={12} />
            </button>
          )}
          <button className="aud-limpar" onClick={() => setF(FILTROS_VAZIOS)}>
            limpar tudo
          </button>
        </div>
      )}

        {/* Erro só na lista (o topo já carregou): a tela inteira não vira um
            ErrorState, mas o problema também não pode ficar mudo — senão uma
            lista vazia por falha parece uma lista vazia por falta de evento. */}
        {erro && !carregando && (
          <p className="form-error">
            {erro}{" "}
            <button className="aud-limpar" onClick={carregarEventos}>
              tentar de novo
            </button>
          </p>
        )}

        {carregando ? (
          <p className="aud-vazio">Carregando…</p>
        ) : eventos.length === 0 ? (
          <p className="aud-vazio">
            {erro ? "Não foi possível carregar a lista." : "Nenhum evento no recorte escolhido."}
            {filtrado && (
              <>
                {" "}
                <button className="aud-limpar" onClick={() => setF(FILTROS_VAZIOS)}>
                  limpar os filtros
                </button>
              </>
            )}
          </p>
        ) : (
          porDia.map(([dia, doDia]) => (
            <div key={dia}>
              <h4 className="aud-dia-head">
                {rotuloDoDia(dia)}
                <span className="aud-dia-qtd">{doDia.length} evento(s)</span>
              </h4>
              <ul className="aud-timeline">
                {doDia.map((e) => {
                  const a = daAcao(e.acao);
                  const recurso = recursoDe(e);
                  return (
                    <li key={e.id} className="aud-evento" onClick={() => setDetalhe(e)}>
                      <span className="aud-evento-hora">{horaDe(e.criado_em)}</span>
                      <span className={"aud-evento-icone " + a.tom}>
                        <a.Icone size={17} />
                      </span>
                      <span className="aud-evento-corpo">
                        <span className="aud-evento-frase">
                          <button
                            className="aud-evento-quem"
                            title="Abrir o dossiê desta pessoa"
                            onClick={(ev) => {
                              ev.stopPropagation();
                              abrirDossie(e.usuario);
                            }}
                          >
                            {e.usuario || "—"}
                          </button>{" "}
                          {a.verbo ? (
                            <>
                              {a.verbo} <b>{recurso || nomeDoSistema(e.sistema)}</b>
                            </>
                          ) : (
                            <>
                              {" · "}
                              <b>{a.rotulo}</b>
                              {recurso ? " · " + recurso : ""}
                            </>
                          )}
                        </span>
                        <span className="aud-evento-meta">
                          <span>{nomeDoSistema(e.sistema)}</span>
                          {e.ip && <span>IP {e.ip}</span>}
                          {recurso && a.verbo && <span>{a.rotulo}</span>}
                        </span>
                      </span>
                      <span className="aud-evento-rel">{relativo(e.criado_em)}</span>
                      <button
                        className="icon-btn"
                        title="Ver os detalhes"
                        onClick={(ev) => {
                          ev.stopPropagation();
                          setDetalhe(e);
                        }}
                      >
                        <IconEye size={16} />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))
        )}
      </section>

      {/* ---------- O DETALHE DE UM EVENTO ---------- */}
      {detalhe && (
        <div className="modal-overlay" onMouseDown={() => setDetalhe(null)}>
          <div className="modal-card" onMouseDown={(ev) => ev.stopPropagation()}>
            <div className="modal-head">
              <div>
                <h3>
                  {daAcao(detalhe.acao).rotulo} · evento #{detalhe.id}
                </h3>
                <p className="modal-sub">
                  {nomeDoSistema(detalhe.sistema)} · {quando(detalhe.criado_em)} ·{" "}
                  {relativo(detalhe.criado_em)}
                </p>
              </div>
              <button className="icon-btn" onClick={() => setDetalhe(null)}>
                <IconClose size={18} />
              </button>
            </div>
            <div className="modal-body">
              <div className="aud-dossie-num aud-num-texto">
                <span className="aud-num" title={detalhe.usuario || ""}>
                  <b>{detalhe.usuario ? <ValorLongo texto={detalhe.usuario} /> : "—"}</b>
                  <span>quem fez</span>
                </span>
                <span className="aud-num" title={recursoDe(detalhe)}>
                  <b>{recursoDe(detalhe) ? <ValorLongo texto={recursoDe(detalhe)} /> : "—"}</b>
                  <span>o que foi mexido</span>
                </span>
                <span className="aud-num" title={detalhe.ip || ""}>
                  <b>{detalhe.ip ? <ValorLongo texto={detalhe.ip} /> : "—"}</b>
                  <span>de onde</span>
                </span>
              </div>

              {/* O `detalhes` é gravado pelo sistema de origem: só os NOMES dos
                  campos mexidos, nunca os valores (para não vazar dado sensível
                  no log central). Por isso ele é mostrado cru. */}
              <h4 className="aud-mini-titulo">O que o sistema registrou</h4>
              <pre className="dev-json">{JSON.stringify(detalhe.detalhes, null, 2)}</pre>
            </div>
            <div className="modal-foot">
              <button className="btn-secondary" onClick={() => abrirDossie(detalhe.usuario)}>
                <IconUserSearch size={15} /> Dossiê de quem fez
              </button>
              {detalhe.ip && (
                <button
                  className="btn-secondary"
                  onClick={() => {
                    mudar({ ip: detalhe.ip });
                    setDetalhe(null);
                  }}
                >
                  <IconGlobe size={15} /> Só deste IP
                </button>
              )}
              {detalhe.recurso_tipo && (
                <button
                  className="btn-secondary"
                  onClick={() => {
                    mudar({ recurso_tipo: detalhe.recurso_tipo, recurso_id: detalhe.recurso_id ?? "" });
                    setDetalhe(null);
                  }}
                >
                  <IconSearch size={15} /> Só deste registro
                </button>
              )}
              <button className="btn-primary" onClick={() => setDetalhe(null)}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------- O DOSSIÊ DE UMA PESSOA ---------- */}
      {dossie && (
        <div className="modal-overlay" onMouseDown={() => setDossie(null)}>
          <div className="modal-card aud-dossie" onMouseDown={(ev) => ev.stopPropagation()}>
            <div className="modal-head">
              <div>
                <h3>{dossie.usuario}</h3>
                <p className="modal-sub">
                  {carregandoDossie || dossie.carregando
                    ? "buscando o histórico…"
                    : "tudo o que fez na suíte · últimos " + dossie.janela + " dias"}
                </p>
              </div>
              <button className="icon-btn" onClick={() => setDossie(null)}>
                <IconClose size={18} />
              </button>
            </div>

            <div className="modal-body">
              {carregandoDossie || dossie.carregando ? (
                <p className="aud-vazio">Carregando…</p>
              ) : (
                <>
                  <div className="aud-dossie-num">
                    <span className="aud-num">
                      <b>{dossie.totais.eventos_periodo}</b>
                      <span>no período</span>
                    </span>
                    <span className="aud-num">
                      <b>{dossie.totais.eventos}</b>
                      <span>desde o início</span>
                    </span>
                    <span className="aud-num">
                      <b>{dossie.totais.sistemas_periodo}</b>
                      <span>sistemas</span>
                    </span>
                    <span className="aud-num">
                      <b>{dossie.totais.ips_periodo}</b>
                      <span>IPs</span>
                    </span>
                    <span className="aud-num">
                      <b>{dossie.totais.exclusoes_periodo}</b>
                      <span>exclusões</span>
                    </span>
                    <span className={"aud-num" + (dossie.totais.falhas_periodo > 0 ? " alerta" : "")}>
                      <b>{dossie.totais.falhas_periodo}</b>
                      <span>falhas de login</span>
                    </span>
                  </div>

                  <FaixaAtividade dados={dossie.porDia ?? []} />

                  <div className="aud-duas" style={{ marginTop: 18 }}>
                    <div>
                      <h4 className="aud-mini-titulo">Em quais sistemas</h4>
                      <Ranking
                        itens={(dossie.porSistema ?? []).map((s: any) => ({
                          chave: s.sistema,
                          nome: nomeDoSistema(s.sistema),
                          valor: s.eventos,
                          nota: s.exclusoes > 0 ? s.exclusoes + " excl." : undefined,
                        }))}
                        aoClicar={(sistema) => {
                          mudar({ sistema, usuario: dossie.usuario });
                          setDossie(null);
                        }}
                      />
                      <h4 className="aud-mini-titulo" style={{ marginTop: 18 }}>
                        O que costuma fazer
                      </h4>
                      <Ranking
                        itens={(dossie.porAcao ?? []).map((a: any) => ({
                          chave: a.acao,
                          nome: daAcao(a.acao).rotulo,
                          valor: a.eventos,
                        }))}
                      />
                    </div>

                    <div>
                      <h4 className="aud-mini-titulo">De onde acessou</h4>
                      {(dossie.porIp ?? []).length === 0 ? (
                        <p className="aud-vazio">Sem IP registrado.</p>
                      ) : (
                        <ul className="aud-ips">
                          {dossie.porIp.map((i: any) => (
                            <li key={i.ip}>
                              <IconGlobe size={15} />
                              <strong>{i.ip}</strong>
                              <span className="aud-ip-nota">
                                {i.eventos} evento(s)
                                <br />
                                de {quando(i.primeiro)} a {quando(i.ultimo)}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}

                      <h4 className="aud-mini-titulo" style={{ marginTop: 18 }}>
                        Últimos passos
                      </h4>
                      <ul className="aud-timeline">
                        {(dossie.ultimos ?? []).slice(0, 8).map((e: any) => {
                          const a = daAcao(e.acao);
                          return (
                            <li key={e.id} className="aud-evento" onClick={() => setDetalhe(e)}>
                              <span className={"aud-evento-icone " + a.tom}>
                                <a.Icone size={15} />
                              </span>
                              <span className="aud-evento-corpo">
                                <span className="aud-evento-frase">
                                  {a.verbo ? (
                                    <>
                                      {a.verbo} <b>{recursoDe(e) || nomeDoSistema(e.sistema)}</b>
                                    </>
                                  ) : (
                                    <>
                                      <b>{a.rotulo}</b>
                                      {recursoDe(e) ? " · " + recursoDe(e) : ""}
                                    </>
                                  )}
                                </span>
                                <span className="aud-evento-meta">
                                  <span>{nomeDoSistema(e.sistema)}</span>
                                  <span>{quando(e.criado_em)}</span>
                                </span>
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="modal-foot">
              <button
                className="btn-secondary"
                onClick={() => {
                  mudar({ usuario: dossie.usuario, sistema: "todos", acao: "todos", horas: "2160" });
                  setDossie(null);
                }}
              >
                <IconFilter size={15} /> Ver só os eventos desta pessoa
              </button>
              <button
                className="btn-secondary"
                title="Baixar TODA a auditoria desta pessoa, sem limite de período"
                onClick={() => exportar({ usuario: dossie.usuario })}
              >
                <IconDownload size={15} /> Exportar tudo dela
              </button>
              <button className="btn-primary" onClick={() => setDossie(null)}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
